import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom'
import { AuthProvider } from '../context/AuthContext'
import { CartProvider } from '../context/CartContext'
import { ProfileProvider } from '../context/ProfileContext'
import { TenantProvider } from '../context/TenantContext'
import { Overlays } from '../ui/Overlays'
import { useTelemetryTracker } from '../ui/hooks/useTelemetryTracker'
import { AccountLayout } from '../ui/components/layouts/AccountLayout'
import { DashboardShell } from '../ui/components/layouts/DashboardShell'
import { AuthLayout } from '../ui/components/layouts/AuthLayout'
import { MainLayout } from '../ui/components/layouts/MainLayout'
import { HomePage } from '@store/HomePage'
import { storeConfig } from '@store/store.config'
import { ShopProductPage } from '../ui/pages/shop/ShopProductPage'
import { ShopListPage } from '../ui/pages/shop/ShopListPage'
import { BuildListPage } from '../ui/pages/build/BuildListPage'
import { BuildProductPage } from '../ui/pages/build/BuildProductPage'
import { NotFoundPage } from '../ui/pages/NotFoundPage'
import { BabyRockPoc } from '../ui/pages/_poc/BabyRockPoc'
import { RegisterPage } from '../ui/pages/auth/RegisterPage'
import { StaffWelcomePage } from '../ui/pages/auth/StaffWelcomePage'
import { UnifiedLoginPage } from '../ui/pages/auth/UnifiedLoginPage'
import { AccountDashboardPage } from '../ui/pages/account/AccountDashboardPage'
import { AccountOrderDetailPage } from '../ui/pages/account/AccountOrderDetailPage'
import { AccountOrdersPage } from '../ui/pages/account/AccountOrdersPage'
import { AccountProfilePage } from '../ui/pages/account/AccountProfilePage'
import { AccountReturnsPage } from '../ui/pages/account/AccountReturnsPage'
import { AccountTestimonyPage } from '../ui/pages/account/AccountTestimonyPage'
import { DashboardPage } from '../ui/pages/dashboard/DashboardPage'
import { TrafficDashboardPage } from '../ui/pages/dashboard/TrafficDashboardPage'
import { SettingsPage } from '../ui/pages/preferences/SettingsPage'
import { NotificationTemplateEditPage } from '../ui/pages/preferences/NotificationTemplateEditPage'
import { NotificationTemplatesPage } from '../ui/pages/preferences/NotificationTemplatesPage'
import { WebhookProvidersPage } from '../ui/pages/preferences/WebhookProvidersPage'
import { IamGroupsPage } from '../ui/pages/iam/IamGroupsPage'
import { IamScreenActionsPage } from '../ui/pages/iam/IamScreenActionsPage'
import { StaffPage } from '../ui/pages/iam/StaffPage'
import { UserTypesPage } from '../ui/pages/iam/UserTypesPage'
import { AttributesPage } from '../ui/pages/manage/AttributesPage'
import { CategoriesPage } from '../ui/pages/manage/CategoriesPage'
import { CustomDataTypesPage } from '../ui/pages/manage/CustomDataTypesPage'
import { ProductsPage } from '../ui/pages/products/ProductsPage'
import { ProductVariantPricingPage } from '../ui/pages/products/ProductVariantPricingPage'
import { ProductVariantPricingHistoryPage } from '../ui/pages/products/ProductVariantPricingHistoryPage'
import { RegisteredCustomersPage } from '../ui/pages/customers/RegisteredCustomersPage'
import { ProductPricingPage } from '../ui/pages/products/ProductPricingPage'
import { ProductComponentsPage } from '../ui/pages/products/ProductComponentsPage'
import { SolutionsPage } from '../ui/pages/products/SolutionsPage'
import { ProductSourcePage } from '../ui/pages/products/ProductSourcePage'
import { ProductStockHistoryPage } from '../ui/pages/warehouse/ProductStockHistoryPage'
import { VariantStockHistoryPage } from '../ui/pages/warehouse/VariantStockHistoryPage'
import { ProductPurchasesPage } from '../ui/pages/warehouse/ProductPurchasesPage'
import { RawMaterialsPage } from '../ui/pages/manufacturing/RawMaterialsPage'
import { ManufacturingPricingPage } from '../ui/pages/manufacturing/ManufacturingPricingPage'
import { MaterialPurchasesPage } from '../ui/pages/manufacturing/MaterialPurchasesPage'
import { RawMaterialStockHistoryPage } from '../ui/pages/manufacturing/RawMaterialStockHistoryPage'
import { SuppliersPage } from '../ui/pages/manage/SuppliersPage'
import { ManagePurchasesPage } from '../ui/pages/manage/ManagePurchasesPage'
import { GstRatesPage } from '../ui/pages/manage/GstRatesPage'
import { UnitsPage } from '../ui/pages/manage/UnitsPage'
import { StoresPage } from '../ui/pages/manage/StoresPage'
import { OrganisationsPage } from '../ui/pages/manage/OrganisationsPage'
import { ProductSuppliersPage } from '../ui/pages/manage/ProductSuppliersPage'
import { MaterialSuppliersPage } from '../ui/pages/manage/MaterialSuppliersPage'
import { OrderManagementOrdersPage } from '../ui/pages/orderManagement/OrderManagementOrdersPage'
import { OrderManagementReturnsPage } from '../ui/pages/orderManagement/OrderManagementReturnsPage'
import { BuildRequestsPage } from '../ui/pages/orderManagement/BuildRequestsPage'
import { PrivateDashboardRoute } from './PrivateDashboardRoute'
import { ROUTES } from '../constants/routes.constants'
import { PrivateRoute } from './PrivateRoute'
import { TenantApexGuard } from './TenantApexGuard'

/**
 * Uyyamadilu route table — covers only the screens that are
 * implemented today.
 *
 * Per Kiran 2026-05-15 cleanup (SOP 15 Ch.15 + SOP 16 §"2026-05-15
 * Infrastructure dead-code purge"): every placeholder route was
 * removed alongside its supporting D1 tables. When a phase ships,
 * its routes return — added next to the new page component, not
 * pre-declared.
 */
export function AppRouter(): JSX.Element {
  // BrowserRouter must be the outermost wrapper: CartProvider renders the
  // CartOverlay as a sibling of its children, and the overlay calls
  // useNavigate() ("Continue shopping" button). Without Router context
  // above CartProvider, useNavigate throws and the overlay never mounts —
  // which is exactly the "click cart → blank, nothing works" symptom.
  return (
    <BrowserRouter>
      <TenantProvider>
        <AuthProvider>
          <CartProvider>
            <ProfileProvider>
              <Overlays />
              <TelemetryMount />
            <Routes>
              {/* ---------- PUBLIC ---------- */}
              <Route element={<HomeLayoutOutlet />}>
                <Route path={ROUTES.HOME} element={<HomePage />} />
              </Route>
              <Route element={<MainLayoutOutlet />}>
                {/* /shop = product list (search + filters); /shop/:id = detail (2026-06-05). */}
                <Route path={ROUTES.SHOP} element={<ShopListPage />} />
                <Route path={`${ROUTES.SHOP}/:id`} element={<ShopProductPage />} />
                {/* /build = parallel surface for the future store-builder
                    flow (select products → publish sub-domain). Auth-
                    gated (Kiran 2026-06-06) — signed-out visitors bounce
                    to /login with `from` preserved; the storefront nav
                    only shows the Build pill once authed. Same data as
                    /shop, separate URL + API + components. */}
                <Route
                  path={ROUTES.BUILD}
                  element={
                    <TenantApexGuard>
                      <PrivateRoute>
                        <BuildListPage />
                      </PrivateRoute>
                    </TenantApexGuard>
                  }
                />
                <Route
                  path={`${ROUTES.BUILD}/:id`}
                  element={
                    <TenantApexGuard>
                      <PrivateRoute>
                        <BuildProductPage />
                      </PrivateRoute>
                    </TenantApexGuard>
                  }
                />
              </Route>

              {/* DEV PROOF-OF-CONCEPT — remove after review (not a real route) */}
              <Route path="/anim-poc" element={<BabyRockPoc />} />

              {/* ---------- AUTH ---------- */}
              {/* Unified sign-in for customer AND admin. */}
              <Route path={ROUTES.LOGIN} element={<UnifiedLoginPage />} />
              <Route path={ROUTES.ACCOUNTLOGIN} element={<Navigate to={ROUTES.LOGIN} replace />} />
              {/* Legacy redirect: old bookmarks to /admin/login bounce to /login */}
              <Route path="/admin/login" element={<Navigate to={ROUTES.LOGIN} replace />} />
              <Route path="/dashboard/login" element={<Navigate to={ROUTES.LOGIN} replace />} />

              <Route element={<AuthLayoutOutlet />}>
                <Route path={ROUTES.ACCOUNTREGISTER} element={<RegisterPage />} />
                {/* Staff first-login welcome — public, token-gated. */}
                <Route path={ROUTES.STAFFWELCOME} element={<StaffWelcomePage />} />
              </Route>

              {/* ---------- ACCOUNT (sidebar layout, auth required) ---------- */}
              <Route element={<AccountLayoutOutlet />}>
                <Route path={ROUTES.ACCOUNT} element={<AccountDashboardPage />} />
                <Route path={ROUTES.ACCOUNTPROFILE} element={<AccountProfilePage />} />
                <Route path={ROUTES.ACCOUNTORDERS} element={<AccountOrdersPage />} />
                <Route path={ROUTES.ACCOUNTORDERDETAIL} element={<AccountOrderDetailPage />} />
                <Route path={ROUTES.ACCOUNTRETURNS} element={<AccountReturnsPage />} />
                <Route path={ROUTES.ACCOUNTTESTIMONY} element={<AccountTestimonyPage />} />
              </Route>

              {/* ---------- DASHBOARD / IAM — one DashboardShell ---------- */}
              <Route element={<DashboardShellOutlet />}>
                <Route path={ROUTES.DASHBOARD} element={<DashboardPage />} />
                <Route path={ROUTES.DASHBOARDTRAFFIC} element={<TrafficDashboardPage />} />
                <Route path={ROUTES.PREFERENCESSETTINGS} element={<SettingsPage />} />

                {/* /iam has no overview screen — bookmark redirects to user-types. */}
                <Route path={ROUTES.IAM} element={<Navigate to={ROUTES.IAMUSERTYPES} replace />} />
                {/* Legacy /admin → /dashboard. Bookmarked operator URLs keep working. */}
                <Route path="/admin" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
                <Route path="/admin/*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
                <Route path={ROUTES.IAMUSERTYPES} element={<UserTypesPage />} />
                <Route path={ROUTES.IAMSCREENACTIONS} element={<IamScreenActionsPage />} />
                <Route path={ROUTES.IAMGROUPS} element={<IamGroupsPage />} />
                <Route
                  path={ROUTES.PREFERENCESEMAILTEMPLATES}
                  element={<NotificationTemplatesPage />}
                />
                <Route
                  path={ROUTES.PREFERENCESEMAILTEMPLATEEDIT}
                  element={<NotificationTemplateEditPage />}
                />
                <Route
                  path={ROUTES.PREFERENCESWEBHOOKPROVIDERS}
                  element={<WebhookProvidersPage />}
                />
                <Route path={ROUTES.IAMSTAFF} element={<StaffPage />} />

                {/* Manage — operational CRUD screens */}
                <Route path={ROUTES.MANAGEATTRIBUTES} element={<AttributesPage />} />
                <Route path={ROUTES.MANAGECUSTOMDATATYPES} element={<CustomDataTypesPage />} />
                <Route path={ROUTES.MANAGECATEGORIES} element={<CategoriesPage />} />
                <Route path={ROUTES.MANAGEPURCHASES} element={<ManagePurchasesPage />} />
                <Route path={ROUTES.MANAGEGSTRATES} element={<GstRatesPage />} />
                <Route path={ROUTES.MANAGEUNITS} element={<UnitsPage />} />
                <Route path={ROUTES.MANAGESTORES} element={<StoresPage />} />
                <Route path={ROUTES.MANAGEORGANISATIONS} element={<OrganisationsPage />} />
                <Route path={ROUTES.MANAGEPRODUCTSUPPLIERS} element={<ProductSuppliersPage />} />
                <Route path={ROUTES.MANAGEMATERIALSUPPLIERS} element={<MaterialSuppliersPage />} />

                {/* Product — own worker + nav group as of 2026-05-17 */}
                <Route path={ROUTES.PRODUCTS} element={<ProductsPage />} />
                <Route path={ROUTES.PRODUCTVARIANTPRICING} element={<ProductVariantPricingPage />} />
                <Route path={ROUTES.PRODUCTVARIANTPRICINGHISTORY} element={<ProductVariantPricingHistoryPage />} />
                <Route path={ROUTES.PRODUCTPRICING} element={<ProductPricingPage />} />
                <Route path={ROUTES.PRODUCTCOMPONENTS} element={<ProductComponentsPage />} />
                <Route path={ROUTES.SOLUTIONS} element={<SolutionsPage />} />
                <Route path={ROUTES.PRODUCTSOURCE} element={<ProductSourcePage />} />

                {/* Customers — read-only operator visibility (2026-05-17) */}
                <Route path={ROUTES.CUSTOMERSREGISTERED} element={<RegisteredCustomersPage />} />

                {/* Manufacturing — supplier + raw-material catalogues + pricing + variant mapping (2026-05-19) */}
                <Route path={ROUTES.MANAGESUPPLIERS} element={<SuppliersPage />} />
                <Route path={ROUTES.MANUFACTURINGRAWMATERIALS} element={<RawMaterialsPage />} />
                <Route path={ROUTES.MANUFACTURINGPRICING} element={<ManufacturingPricingPage />} />
                <Route path={ROUTES.MANUFACTURINGMATERIALPURCHASES} element={<MaterialPurchasesPage />} />
                <Route path={ROUTES.MANUFACTURINGRAWMATERIALSTOCKHISTORY} element={<RawMaterialStockHistoryPage />} />

                {/* Warehouse — per-variant stock movement ledger + Product Purchases
                    (with Receive flow, 2026-06-03). Stock Entry retired alongside. */}
                <Route path={ROUTES.WAREHOUSEPRODUCTSTOCKHISTORY} element={<ProductStockHistoryPage />} />
                <Route path={ROUTES.WAREHOUSEVARIANTSTOCKHISTORY} element={<VariantStockHistoryPage />} />
                <Route path={ROUTES.WAREHOUSEPRODUCTPURCHASES} element={<ProductPurchasesPage />} />

                {/* Order Management — operator views of customer orders + returns (2026-06-05) */}
                <Route path={ROUTES.ORDERMANAGEMENTORDERS}        element={<OrderManagementOrdersPage />} />
                <Route path={ROUTES.ORDERMANAGEMENTRETURNS}       element={<OrderManagementReturnsPage />} />
                <Route path={ROUTES.ORDERMANAGEMENTBUILDREQUESTS} element={<BuildRequestsPage />} />
              </Route>

              {/* ---------- 404 ---------- */}
              <Route element={<MainLayoutOutlet />}>
                <Route path={ROUTES.NOTFOUND} element={<NotFoundPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
              </Routes>
            </ProfileProvider>
          </CartProvider>
        </AuthProvider>
      </TenantProvider>
    </BrowserRouter>
  )
}

// React Router v6 nested-layout outlets — small wrappers so we don't repeat
// the layout component inside each Route element.

/** Storefront visitor-telemetry trigger — mounted inside BrowserRouter
 *  so the hook can read useLocation. The hook fires a pageview on every
 *  route change + a sendBeacon exit on pagehide. Returns null so it
 *  renders nothing. Kiran 2026-06-30. */
function TelemetryMount(): null {
  return useTelemetryTracker()
}

function MainLayoutOutlet(): JSX.Element {
  return (
    <MainLayout>
      <Outlet />
    </MainLayout>
  )
}

// Home only — enables the slow Ken-Burns drift on the background image
// (the rest of the public/app pages use the static backdrop).
//
// Kiran 2026-07-05 (Phase 1 multi-store refactor): honours
// storeConfig.chrome.home. `'none'` renders bare — no MainLayout, no
// drift, no backdrop — so a new store's HomePage can lay itself out
// full-bleed with zero shared chrome. `'minimal'` and `'default'` keep
// today's MainLayout; the difference between them is picked up inside
// the HomePage component itself (brand-mark-only nav vs full nav).
function HomeLayoutOutlet(): JSX.Element {
  if (storeConfig.chrome.home === 'none') {
    return <Outlet />
  }
  return (
    <MainLayout drift>
      <Outlet />
    </MainLayout>
  )
}

function AuthLayoutOutlet(): JSX.Element {
  return (
    <AuthLayout>
      <Outlet />
    </AuthLayout>
  )
}

function AccountLayoutOutlet(): JSX.Element {
  return (
    <PrivateRoute>
      <AccountLayout>
        <Outlet />
      </AccountLayout>
    </PrivateRoute>
  )
}

function DashboardShellOutlet(): JSX.Element {
  // TenantApexGuard wraps the dashboard shell so any /dashboard, /iam,
  // /manage, /products, /manufacturing, /warehouse, /order-management,
  // /preferences, /customers route hit on a tenant subdomain bounces
  // the operator back to the apex (Phase 3, Kiran 2026-06-07).
  return (
    <TenantApexGuard>
      <PrivateDashboardRoute>
        <DashboardShell>
          <Outlet />
        </DashboardShell>
      </PrivateDashboardRoute>
    </TenantApexGuard>
  )
}
